import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";

import { useAcceptTosMutation } from "@/api/auth/useAcceptTosMutation";
import { useMeQuery } from "@/api/auth/useMeQuery";
import { Logo } from "@/components/brand/Logo";
import LoadingScreen from "@/components/feedback/LoadingScreen";
import Button from "@/components/form/Button";
import Layout from "@/components/layout/Layout";
import { useAuthStore } from "@/stores/auth.store";

export const Route = createFileRoute("/accept-tos")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isLoading, isError } = useMeQuery();
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const acceptTos = useAcceptTosMutation();

  if (!token || isError) {
    return <Navigate to="/login" />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (data?.user.tosAcceptedAt) {
    return <Navigate to="/" />;
  }

  return (
    <Layout>
      <div className="h-full flex items-center justify-center px-4">
        <div className="bg-base/90 backdrop-blur-2xl border border-border w-full max-w-2xl p-8 rounded-2xl shadow-2xl space-y-6">
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <Logo size="md" className="text-tertiary" />
            </div>
            <h1 className="text-2xl font-bold text-primary mt-4">
              Warunki korzystania z usługi
            </h1>
            <p className="text-sm text-secondary mt-2">
              Aby kontynuować, musisz zaakceptować regulamin platformy Gen.mov.
            </p>
          </div>

          <div className="bg-surface-hover rounded-lg p-6 max-h-96 overflow-y-auto text-sm text-secondary space-y-4">
            <h2 className="font-semibold text-primary">1. Postanowienia ogólne</h2>
            <p>
              Platforma Gen.mov umożliwia generowanie treści wideo z wykorzystaniem sztucznej
              inteligencji. Korzystanie z platformy wymaga aktywnej subskrypcji i dostępnych
              kredytów.
            </p>

            <h2 className="font-semibold text-primary">2. Zasady użytkowania</h2>
            <p>
              Użytkownik zobowiązuje się do korzystania z platformy zgodnie z obowiązującym prawem.
              Zabrania się generowania treści naruszających prawa osób trzecich, zawierających
              przemoc, pornografię, mowę nienawiści lub dezinformację.
            </p>

            <h2 className="font-semibold text-primary">3. Kredyty i płatności</h2>
            <p>
              Kredyty są naliczane za każde wygenerowane wideo. W przypadku nieudanego
              generowania kredyty są automatycznie zwracane. Niewykorzystane kredyty z subskrypcji
              nie przechodzą na kolejny okres rozliczeniowy.
            </p>

            <h2 className="font-semibold text-primary">4. Prawa do wygenerowanych treści</h2>
            <p>
              Wygenerowane treści wideo mogą być wykorzystywane przez użytkownika do celów
              komercyjnych i niekomercyjnych, z zastrzeżeniem ograniczeń wynikających z warunków
              licencji dostawcy AI (Google Veo).
            </p>

            <h2 className="font-semibold text-primary">5. Polityka prywatności</h2>
            <p>
              Przetwarzamy dane osobowe zgodnie z RODO. Dane są wykorzystywane wyłącznie w celu
              świadczenia usługi, obsługi płatności i komunikacji z użytkownikiem.
            </p>

            <h2 className="font-semibold text-primary">6. Polityka refundów</h2>
            <p>
              Nieudane generowania wideo są automatycznie refundowane. Refundacja subskrypcji jest
              możliwa w ciągu 14 dni od zakupu, pod warunkiem niewykorzystania kredytów.
            </p>
          </div>

          <Button
            isLoading={acceptTos.isPending}
            onClick={() => {
              acceptTos.mutate(undefined, {
                onSuccess: () => {
                  void navigate({ to: "/" });
                },
              });
            }}
          >
            Akceptuję warunki korzystania z usługi
          </Button>
        </div>
      </div>
    </Layout>
  );
}
